import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createTableRowNode } from "@lexical/table";
import { mergeRegister } from "@lexical/utils";
import { $getNodeByKey, COMMAND_PRIORITY_EDITOR } from "lexical";
import { useEffect } from "react";
import { $createDataTableCellNode } from "../nodes/data-table-cell-node";
import { $createDataTableNode, $isDataTableNode } from "../nodes/data-table-node";
import {
  INSERT_DATA_TABLE_COLUMN_COMMAND,
  INSERT_DATA_TABLE_COMMAND,
  INSERT_DATA_TABLE_ROW_COMMAND,
  SET_DATA_TABLE_BACKGROUND_COLOUR_COMMAND,
  SET_DATA_TABLE_BORDER_COMMAND,
  SET_DATA_TABLE_CELL_PADDING_COMMAND,
} from "../types/commands";
import { useLexicalTable } from "./use-lexical-table";

import type { FC } from "react";

export const DataTablePlugin: FC = () => {
  const [editor] = useLexicalComposerContext();

  const { createTableNode, insertColumn, insertRow } = useLexicalTable();

  useEffect(
    () =>
      mergeRegister(
        editor.registerCommand(
          INSERT_DATA_TABLE_COMMAND,
          ({ columns, rows, border, cellPadding }) => {
            const table = createTableNode(
              { columns, rows },
              {
                createCell: $createDataTableCellNode,
                createRow: $createTableRowNode,
                createTable: () => $createDataTableNode(border, cellPadding),
              },
            );

            return Boolean(table);
          },
          1,
        ),
        editor.registerCommand(
          INSERT_DATA_TABLE_COLUMN_COMMAND,
          ({ insertAfter }) => {
            insertColumn(insertAfter, { createCell: $createDataTableCellNode });
            return true;
          },
          COMMAND_PRIORITY_EDITOR,
        ),
        editor.registerCommand(
          INSERT_DATA_TABLE_ROW_COMMAND,
          ({ position }) => {
            insertRow(position, { createCell: $createDataTableCellNode });
            return true;
          },
          COMMAND_PRIORITY_EDITOR,
        ),
        editor.registerCommand(
          SET_DATA_TABLE_CELL_PADDING_COMMAND,
          ({ nodeKey, padding, skipHistory }) => {
            editor.update(
              () => {
                const tableNode = $getNodeByKey(nodeKey);
                if (!$isDataTableNode(tableNode)) {
                  return false;
                }
                tableNode.setCellPadding(padding);
              },
              skipHistory ? { tag: "historic" } : {},
            );
            return true;
          },
          COMMAND_PRIORITY_EDITOR,
        ),
        editor.registerCommand(
          SET_DATA_TABLE_BACKGROUND_COLOUR_COMMAND,
          ({ nodeKey, colour, skipHistory }) => {
            editor.update(
              () => {
                const tableNode = $getNodeByKey(nodeKey);
                if (!$isDataTableNode(tableNode)) {
                  return false;
                }
                tableNode.setCellBackgroundColour(colour);
              },
              skipHistory ? { tag: "historic" } : {},
            );
            return true;
          },
          COMMAND_PRIORITY_EDITOR,
        ),
        editor.registerCommand(
          SET_DATA_TABLE_BORDER_COMMAND,
          ({ nodeKey, border, skipHistory }) => {
            editor.update(
              () => {
                const tableNode = $getNodeByKey(nodeKey);
                if (!$isDataTableNode(tableNode)) {
                  return false;
                }
                const currentBorder = tableNode.getBorder();
                tableNode.setBorder({
                  width: border.width ?? currentBorder.width,
                  style: border.style ?? currentBorder.style,
                  colour: border.colour ?? currentBorder.colour,
                });
              },
              skipHistory ? { tag: "historic" } : {},
            );
            return true;
          },
          COMMAND_PRIORITY_EDITOR,
        ),
      ),
    [editor, createTableNode, insertColumn, insertRow],
  );

  return null;
};
